// src/utils/emailConfigUtils.ts
import {
	CompanyEmailConfig,
	UserEmailConfig,
	SMTP_PROVIDERS,
	SMTPProvider,
	EmailConfigRequest
} from '../types/communications/emailConfig';

// Validation utilities
export const validateEmail = (email: string): boolean => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

export const validatePort = (port: number): boolean => {
	return port >= 1 && port <= 65535;
};

export const validateEmailConfigForm = (config: Partial<EmailConfigRequest>): {
	isValid: boolean;
	errors: Record<string, string>;
} => {
	const errors: Record<string, string> = {};

	if (!config.name?.trim()) {
		errors.name = 'Configuration name is required';
	}

	if (!config.host?.trim()) {
		errors.host = 'SMTP host is required';
	}

	if (!config.port || !validatePort(config.port)) {
		errors.port = 'Port must be between 1 and 65535';
	}

	if (!config.encryption || !['tls', 'ssl'].includes(config.encryption)) {
		errors.encryption = 'Encryption type must be TLS or SSL';
	}

	if (!config.username?.trim()) {
		errors.username = 'Username is required';
	}

	if (!config.password?.trim()) {
		errors.password = 'Password is required';
	}

	if (!config.from_address?.trim()) {
		errors.from_address = 'From address is required';
	} else if (!validateEmail(config.from_address)) {
		errors.from_address = 'Invalid email address format';
	}

	if (!config.from_name?.trim()) {
		errors.from_name = 'From name is required';
	}

	return {
		isValid: Object.keys(errors).length === 0,
		errors
	};
};

// SMTP Provider utilities
export const getProviderByName = (name: string): SMTPProvider | undefined => {
	return SMTP_PROVIDERS.find(provider => provider.name === name);
};

export const detectProvider = (host: string): SMTPProvider | undefined => {
	const hostname = host.toLowerCase();

	if (hostname.includes('gmail')) {
		return getProviderByName('Gmail');
	}
	if (hostname.includes('outlook') || hostname.includes('hotmail') || hostname.includes('live')) {
		return getProviderByName('Outlook/Hotmail');
	}
	if (hostname.includes('yahoo')) {
		return getProviderByName('Yahoo');
	}

	return getProviderByName('Custom SMTP');
};

export const applyProviderPreset = (
	provider: SMTPProvider,
	currentConfig: Partial<EmailConfigRequest>
): Partial<EmailConfigRequest> => {
	return {
		...currentConfig,
		host: provider.host || currentConfig.host,
		port: provider.port || currentConfig.port,
		encryption: provider.encryption || currentConfig.encryption,
	};
};

// Configuration status utilities
export const getConfigStatus = (config: CompanyEmailConfig | UserEmailConfig): {
	status: 'active' | 'inactive' | 'unverified';
	statusText: string;
	statusColor: string;
} => {
	if (!config.is_verified) {
		return {
			status: 'unverified',
			statusText: 'Unverified',
			statusColor: 'text-red-600'
		};
	}

	if (config.is_active) {
		return {
			status: 'active',
			statusText: 'Active',
			statusColor: 'text-green-600'
		};
	}

	return {
		status: 'inactive',
		statusText: 'Inactive',
		statusColor: 'text-gray-600'
	};
};

export const canActivateConfig = (config: CompanyEmailConfig | UserEmailConfig): boolean => {
	return config.is_verified && !config.is_active;
};

// Security and privacy utilities
export const maskPassword = (password: string): string => {
	if (!password) return '';
	if (password.length <= 4) return '****';
	return password.substring(0, 2) + '*'.repeat(password.length - 4) + password.substring(password.length - 2);
};

export const getSecurityLevel = (config: EmailConfigRequest): {
	level: 'low' | 'medium' | 'high';
	issues: string[];
	recommendations: string[];
} => {
	const issues: string[] = [];
	const recommendations: string[] = [];
	let level: 'low' | 'medium' | 'high' = 'high';

	// Check encryption
	if (config.encryption === 'ssl') {
		issues.push('Using older SSL encryption');
		recommendations.push('Consider upgrading to TLS encryption for better security');
		level = 'medium';
	}

	// Check port security
	if (config.port === 25) {
		issues.push('Using unencrypted SMTP port');
		recommendations.push('Use port 587 (TLS) or 465 (SSL) for encrypted communication');
		level = 'low';
	}

	// Check for common insecure patterns
	if (config.host?.toLowerCase().includes('localhost') || config.host?.includes('127.0.0.1')) {
		issues.push('Using localhost configuration');
		recommendations.push('Ensure this is intended for development only');
	}

	if (!config.password || config.password.length < 8) {
		issues.push('Weak password detected');
		recommendations.push('Use a strong password or app-specific password');
		level = 'low';
	}

	return { level, issues, recommendations };
};

// Testing utilities
export const generateTestEmailContent = (configName: string, fromAddress: string): {
	subject: string;
	body: string;
} => {
	const timestamp = new Date().toLocaleString();

	return {
		subject: `Test Email from ${configName}`,
		body: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
                    Email Configuration Test
                </h2>
                <div style="background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
                    <p><strong>✅ Success!</strong> Your email configuration is working properly.</p>
                </div>
                <div style="margin: 20px 0;">
                    <p><strong>Configuration:</strong> ${configName}</p>
                    <p><strong>From Address:</strong> ${fromAddress}</p>
                    <p><strong>Test Time:</strong> ${timestamp}</p>
                </div>
                <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
                    This is an automated test email from your notification system.
                </div>
            </div>
        `.trim()
	};
};

// Sorting and filtering utilities
export const sortConfigsByStatus = (configs: (CompanyEmailConfig | UserEmailConfig)[]): (CompanyEmailConfig | UserEmailConfig)[] => {
	return [...configs].sort((a, b) => {
		// Active verified configs first
		if (a.is_active && a.is_verified && !(b.is_active && b.is_verified)) return -1;
		if (!(a.is_active && a.is_verified) && b.is_active && b.is_verified) return 1;

		// Then verified but inactive configs
		if (a.is_verified && !a.is_active && !b.is_verified) return -1;
		if (!a.is_verified && b.is_verified && !b.is_active) return 1;

		// Finally by creation date (newest first)
		return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
	});
};

export const filterConfigsBySearch = (
	configs: (CompanyEmailConfig | UserEmailConfig)[],
	searchTerm: string
): (CompanyEmailConfig | UserEmailConfig)[] => {
	if (!searchTerm.trim()) return configs;

	const term = searchTerm.toLowerCase();
	return configs.filter(config =>
		config.name.toLowerCase().includes(term) ||
		config.host.toLowerCase().includes(term) ||
		config.from_address.toLowerCase().includes(term) ||
		config.from_name.toLowerCase().includes(term)
	);
};

// Export format utilities
export const exportConfigsToCSV = (configs: (CompanyEmailConfig | UserEmailConfig)[]): string => {
	const headers = ['Name', 'Host', 'Port', 'Encryption', 'From Address', 'From Name', 'Status', 'Created'];
	const rows = configs.map(config => [
		config.name,
		config.host,
		config.port.toString(),
		config.encryption,
		config.from_address,
		config.from_name,
		config.is_active ? 'Active' : 'Inactive',
		new Date(config.created_at).toLocaleDateString()
	]);

	return [headers, ...rows]
		.map(row => row.map(field => `"${field}"`).join(','))
		.join('\n');
};

// Configuration comparison utilities
export const compareConfigs = (
	config1: CompanyEmailConfig | UserEmailConfig,
	config2: CompanyEmailConfig | UserEmailConfig
): {
	differences: Array<{
		field: string;
		value1: any;
		value2: any;
	}>;
	identical: boolean;
} => {
	const fields = ['name', 'host', 'port', 'encryption', 'username', 'from_address', 'from_name'];
	const differences: Array<{ field: string; value1: any; value2: any }> = [];

	fields.forEach(field => {
		const key = field as keyof typeof config1;
		if (config1[key] !== config2[key]) {
			differences.push({
				field,
				value1: config1[key],
				value2: config2[key]
			});
		}
	});

	return {
		differences,
		identical: differences.length === 0
	};
};